import { Component, OnDestroy, OnInit } from '@angular/core';
import {
    BreadcrumbNavigationService,
    ConfigurationService,
    CoursesService,
    TrackingV2Service,
    UserManagementService
} from '@draeger-academy/common';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { MyCourseItem } from '../../../../../../models/myCourse';

@Component({
    selector: 'da-account-landing',
    templateUrl: './account-landing.component.html',
    styleUrls: ['./account-landing.component.scss'],
    standalone: false
})
export class AccountLandingComponent implements OnInit, OnDestroy {
    public courseDetail: string;
    public coursesPerPage = 2;
    private subModuleCourseList: string[];
    private _$showMoreInformation: BehaviorSubject<boolean> = new BehaviorSubject(false);
    private _$coursesFiltered: BehaviorSubject<MyCourseItem[]> = new BehaviorSubject<MyCourseItem[]>(null);
    private _$coursesFilteredMore: BehaviorSubject<MyCourseItem[]> = new BehaviorSubject<MyCourseItem[]>(null);
    private _$title: BehaviorSubject<any> = new BehaviorSubject<any>(null);
    private _$text: BehaviorSubject<any> = new BehaviorSubject<any>(null);
    private _$firstName: BehaviorSubject<any> = new BehaviorSubject<any>(null);
    private _$lastName: BehaviorSubject<any> = new BehaviorSubject<any>(null);
    private _$titleCatalog: BehaviorSubject<any> = new BehaviorSubject<any>(null);
    private _$titleUpcoming: BehaviorSubject<any> = new BehaviorSubject<any>(null);
    private _$catalogs: BehaviorSubject<any> = new BehaviorSubject<any>(null);
    private _$upcomingCourses: BehaviorSubject<any> = new BehaviorSubject<any>(null);
    private _$isPartnerPage: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    private _subscriptionCourses: Subscription;
    private _subscriptionSiteSettings: Subscription;
    private _subscriptionLanguageSwitched: Subscription;
    private _$loading: BehaviorSubject<boolean> = new BehaviorSubject(true);

    private _$categories: BehaviorSubject<{}> = new BehaviorSubject<{}>(null);
    private _$testResults: BehaviorSubject<{}> = new BehaviorSubject<{}>(null);

    constructor(
        private _configurationService: ConfigurationService,
        private _translateService: TranslateService,
        private userService: UserManagementService,
        private coursesService: CoursesService
    ) {}

    public get isPartnerPage$(): Observable<any> {
        return this._$isPartnerPage.asObservable();
    }

    public get title$(): Observable<any> {
        return this._$title.asObservable();
    }

    public get firstName$(): Observable<any> {
        return this._$firstName.asObservable();
    }

    public get lastName$(): Observable<any> {
        return this._$lastName.asObservable();
    }

    public get catalogs$(): Observable<any> {
        return this._$catalogs.asObservable();
    }

    public get text$(): Observable<any> {
        return this._$text.asObservable();
    }

    public get titleCatalog$(): Observable<any> {
        return this._$titleCatalog.asObservable();
    }

    public get titleUpcoming$(): Observable<any> {
        return this._$titleUpcoming.asObservable();
    }

    public get coursesFiltered$(): Observable<MyCourseItem[]> {
        return this._$coursesFiltered.asObservable();
    }

    public get coursesFilteredMore$(): Observable<MyCourseItem[]> {
        return this._$coursesFilteredMore.asObservable();
    }

    public get loading$(): Observable<boolean> {
        return this._$loading.asObservable();
    }

    public get categories$(): Observable<{}> {
        return this._$categories.asObservable();
    }

    public get testResults$(): Observable<{}> {
        return this._$testResults.asObservable();
    }

    ngOnDestroy() {
        if (this._subscriptionCourses) {
            this._subscriptionCourses.unsubscribe();
        }
        if (this._subscriptionSiteSettings) {
            this._subscriptionSiteSettings.unsubscribe();
        }

        if (this._subscriptionLanguageSwitched) {
            this._subscriptionLanguageSwitched.unsubscribe();
        }
    }

    ngOnInit() {
        this._$isPartnerPage.next(this._configurationService.isPartnerPage());
        if (this._configurationService.$coursesPerPage) {
            this._subscriptionCourses = this._configurationService.$coursesPerPage.subscribe((data) => {
                if (data) {
                    this.coursesPerPage = Number(data);
                }
            });
        }

        this.setTranslations();
        this.userService.getUser().then((user) => {
            this._$firstName.next(user.firstName);
            this._$lastName.next(user.lastName);
            this._$catalogs.next(user.catalogs);
            this._$upcomingCourses.next(user.courses);
        });

        this.getCourses();

        this._getCategories().then((cat) => {
            if (cat) {
                this._$categories.next(cat);
            }
        });

        this._getTestResults().then((res) => {
            if (res) {
                this._$testResults.next(res);
            }
        });

        this._subscriptionLanguageSwitched = this.coursesService.languageSelected?.subscribe((language) => {
            if (language) {
                this.setTranslations()
                this._getCategories().then((cat) => {
                    if (cat) {
                        this._$categories.next(cat);
                        this._getTestResults().then((res) => {
                            if (res) {
                                this._$testResults.next(res);
                                this.getCourses();
                            }
                        });
                    }
                });
            }
    });
    }

    public setTranslations() {
        const currentSite = this._configurationService.getSiteFromUrl();
        const url = currentSite ? currentSite.site : 'en_uk';

        if (this._configurationService.isPartnerPage()) {
            this._translateService.stream('account.landing').subscribe((translation) => {
                if (typeof translation !== 'string') {
                    this._$title.next([translation['title']]);
                    this._$text.next([translation['text']]);
                    this._$titleCatalog.next([translation['titleCatalog']]);
                    this._$titleUpcoming.next([translation['titleUpcoming']]);
                }
            });
        } else {
            const promise = this._configurationService.loadConfigurationByCountry(url);
            promise.then((proms) => {
                this._subscriptionSiteSettings = this._configurationService.$siteSettingsByCountry.subscribe((settings) => {
                    if (settings && settings['landing']) {
                        const landing = settings['landing'];
                        this._$title.next([landing['title']]);
                        this._$text.next([landing['content']]);
                        this._$titleCatalog.next([landing['titleCatalog']]);
                        this._$titleUpcoming.next([landing['titleUpcoming']]);
                    }
                });
            });
        }
    }

    public getCourses() {
        this.coursesService.getUserCourses().then(
            (courses) => {
                if (courses.status && courses.status === 404) {
                    this._$coursesFiltered.next([]);
                    this._$coursesFilteredMore.next([]);
                } else if (courses && courses.length > 0) {
                    let coursesFiltered = [];
                    courses.filter((course) => {
                        this._$coursesFiltered.next(courses.filter((c) => c.status === 'BOOKED'));
                        const currentDate = new Date();
                        const endDate = new Date(course.endDate);
                        const courseIsInFuture = endDate < currentDate ? false : true;
                        if (course.isProduct && course.testResult !== 'PASSED') {
                            coursesFiltered.push(course);
                        } else if (!course.isProduct && (course.status === 'OFFEN' || course.status === 'BOOKED') && courseIsInFuture) {
                            coursesFiltered.push(course);
                        }
                    });
                    this._$coursesFiltered.next(coursesFiltered);
                    this.filterCoursesNoSubmoduleCourses(this._$coursesFiltered.getValue());
                    this._$loading.next(false);
                } else {
                    this._$coursesFiltered.next([]);
                    this._$coursesFilteredMore.next([]);
                    this._$loading.next(false);
                }
            },
            (error) => {
                console.log(error);
            }
        );
    }

    private filterCoursesNoSubmoduleCourses(courses) {
        this.subModuleCourseList = [];
        const courseFilteredMore = [];

        courses.forEach((course) => {
            if (course.infoPackageAvailable && course.infoPackageSubModuleIds.length > 0){
                course.infoPackageSubModuleIds.forEach((subModuleCourseId) => {
                    this.subModuleCourseList.push(subModuleCourseId);
                });
            }
        });
        courses.forEach((course) => {
            if (this.subModuleCourseList.indexOf(course.id) === -1 ) {
                courseFilteredMore.push(course);
            }
        });
        this._$coursesFilteredMore.next(courseFilteredMore);
    }

    public showMore($event: boolean) {
        this._$showMoreInformation.next($event);
    }

    private _getCategories(): Promise<any> {
        return new Promise((resolve, reject) => {
            this._translateService.stream('courses.courseStates').subscribe((courseStates) => {
                if (typeof courseStates !== 'string' ) {
                    if (!this._$categories.getValue()) {
                        resolve(courseStates);
                    } else {
                        if (this._$categories.getValue()[0].name !== courseStates[0].name) {
                            resolve(courseStates);
                        }
                    }
                }
            });
        });
    }

    private _getTestResults(): Promise<any> {
        return new Promise((resolve, reject) => {
            this._translateService.stream('courses.testResults').subscribe((testResults) => {
                if (typeof testResults !== 'string') {
                    resolve(testResults);
                }
            });
        });
    }
}
